import React, { useRef, useEffect, useState, useCallback } from "react";
import { Platform, View, StyleSheet, Alert } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { Switch } from "@rneui/themed";
import { DatePickerInput } from "react-native-paper-dates";
import { z } from "zod";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { RichEditor, RichToolbar, actions } from "react-native-pell-rich-editor";
import { File, Remove } from "../../../svgs/CustomCoursework";
import FormLayout from "../common/FormLayout";
import { ActivityState, StoragePathType } from "../../../../graphql/generated/graphql";

import { zodResolver } from "@hookform/resolvers/zod";
import WorksheetForm from "../WorksheetForm/WorksheetForm";
import { VIMEO_REGEX, YOUTUBE_REGEX } from "../../../../utils/regex.dictionary";
import TextEditor from "../../../TextEditor/TextEditor";
import useEditorImageUpload from "../../../../hooks/useEditorImageUpload";
import FileUploadComponent from "../../../file/FileUploadComponent/FileUploadComponent";
import { ProcessedFile } from "../../../../hooks/useFileUpload";

const maybeFormatDueDate = (date) => {
  if (!date) return "";
  if (typeof date === "string" && date.endsWith("Z")) {
    return new Date(date);
  }
  return date;
};

// Helper function to truncate long text
const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const schema = z.object({
  activityName: z.string().min(1, "Activity name is required"),
  description: z.string().optional(),
  videoUrl: z.string().optional(),
  files: z
    .array(
      z.object({
        fileId: z.string(),
        fileName: z.string(),
        fileUrl: z.string(),
        contentType: z.string(),
        size: z.number(),
      })
    )
    .optional(),
  dueDate: z.union([z.string(), z.date()]).optional(),
  enableFileUpload: z.boolean().optional(),
  worksheetId: z.string().optional(),
  activityState: z.string().optional(),
  scheduledDate: z.union([z.string(), z.date()]).optional(),
});

const AssignmentForm = ({ activityData, onSave, onCancel, error, isSubmitting }) => {
  const [uploadError, setUploadError] = useState(null);
  const [worksheetFormOpen, setWorksheetFormOpen] = useState<"open" | "closed" | "error">("closed");

  const [pdfUrl, setPdfUrl] = useState("");

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      activityName: activityData?.title || "",
      description: activityData?.description || "",
      videoUrl: activityData?.videoUrl || "",
      files: activityData?.files || [],
      dueDate: maybeFormatDueDate(activityData?.dueDate) || "",
      enableFileUpload: activityData?.enableFileUpload ?? true,
      worksheetId: activityData?.worksheet?.id,
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
    },
    resolver: zodResolver(schema),
  });

  const editorRef = useRef(null);
  const watchFiles = watch("files");
  const watchActivityState = watch("activityState");

  const isWeb = Platform.OS === "web";

  // Temporary disable image upload until we resolve issues
  const { handleFileUpload } = useEditorImageUpload(editorRef);

  const onPressAddImage = useCallback(async () => {
    const result = await handleFileUpload();
    if (result?.length > 0) {
      return result[0].file.fileURL;
    }
  }, [handleFileUpload]);

  // Initialize the editor with the description content
  useEffect(() => {
    if (editorRef.current && activityData?.description) {
      // Set the initial content once when the component mounts
      editorRef.current.setContentHTML(activityData.description);
    }
    if (activityData?.worksheet) {
      setPdfUrl(activityData.worksheet.files[0]?.fileURL || "");
    }
  }, [activityData]);

  // File upload handling
  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    const newFiles = [
      ...watchFiles,
      processedFilesArray.map((processedFile) => ({
        fileId: processedFile.file.id,
        fileName: processedFile.file.fileName,
        fileUrl: processedFile.file.fileURL,
        contentType: processedFile.mimeType,
        size: processedFile.size,
      })),
    ];
    setValue("files", newFiles);
  };

  // Handle removing a file
  const handleFileRemove = (fileToRemove) => {
    const updatedFiles = watchFiles.filter((file) => file !== fileToRemove);
    setValue("files", updatedFiles);
  };

  const handleAddWorksheet = () => {
    if (isValid) {
      setWorksheetFormOpen("open");
    } else {
      setWorksheetFormOpen("error");
    }
  };

  // Render file list
  const renderFileList = () => {
    return watchFiles.map((file, index) => (
      <View key={index} style={styles.uploadedFile}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <File />
          <CustomSpacing type="horizontal" size="s" />
          <CustomText size="m" weight="ultraBold" text={truncateText(file.fileName, 40)} useTranslationText={false} />
        </View>

        <CustomButton
          leftIcon={<Remove style={{ paddingRight: 4 }} />}
          text="Remove"
          size="s"
          style={{ backgroundColor: "#D0D5DD", borderRadius: 4 }}
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={() => handleFileRemove(file)}
        />
      </View>
    ));
  };

  // Form submission
  const handleCreateAssignment = handleSubmit(async (data) => {
    if (!data.activityName.trim()) {
      return;
    }

    // Create the activity data object with all required fields
    const activityData = {
      title: data.activityName,
      description: data.description,
      videoUrl: data.videoUrl || "",
      files: data.files || [],
      dueDate: data.dueDate,
      enableFileUpload: data.enableFileUpload,
      type: "Assignment",
      worksheetId: data.worksheetId,
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    await onSave(activityData);
  });

  if (worksheetFormOpen === "open") {
    return (
      <WorksheetForm
        onClose={() => setWorksheetFormOpen("closed")}
        activityData={activityData}
        setValue={setValue}
        pdfUrl={pdfUrl}
        setPdfUrl={setPdfUrl}
        onSubmit={handleCreateAssignment}
      />
    );
  }

  return (
    <FormLayout
      title={`${activityData?.id ? "Edit" : "Create"} Assignment`}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleCreateAssignment}
      actionText={activityData?.id ? "Save Assignment" : "Create Assignment"}
      error={error}
      isAssignment={true}
      onAddWorksheet={handleAddWorksheet}
      activityData={activityData}>
      <CustomText text="Assignment Name" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="activityName"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Enter assignment name"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            style={undefined}
          />
        )}
      />
      {errors.activityName && <CustomText text="Activity name is required" style={styles.errorText} />}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Due Date" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="dueDate"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <DatePickerInput
            locale="en"
            value={value}
            onChange={onChange}
            inputMode="start"
            mode="outlined"
            outlineColor={"#D1D5DB"}
            selectionColor={undefined}
            activeOutlineColor={"blue"}
            withDateFormatInLabel={false}
            placeholder="MM/DD/YYYY"
            inputProps={{
              color: "#000000",
              mode: "outline",
            }}
            theme={{
              colors: "#000000",
              roundness: 8,
              opacity: 1,
            }}
            style={styles.datePicker}
            aria-label="Due date date picker"
          />
        )}
      />
      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Activity Description" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="description"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) =>
          isWeb ? (
            <TextEditor
              value={value}
              onChange={(content) => {
                onChange(content);
              }}
              disabled={isSubmitting}
              placeholder="Briefly describe what students will learn in this activity."
              onPressAddImage={onPressAddImage}
            />
          ) : (
            <>
              <RichEditor
                style={styles.editor}
                ref={editorRef}
                initialContentHTML={value}
                onChange={(content) => {
                  onChange(content);
                }}
                placeholder="Enter resource description"
              />
              <RichToolbar
                editor={editorRef}
                actions={[
                  actions.setBold,
                  actions.setItalic,
                  actions.setUnderline,
                  actions.heading1,
                  actions.insertLink,
                  actions.insertImage,
                ]}
                style={styles.toolbar}
                onPressAddImage={onPressAddImage}
              />
            </>
          )
        }
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="File Upload" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Upload files students will need for this assignment" />
      <CustomSpacing type="vertical" size="s" />
      <FileUploadComponent
        storagePath={StoragePathType.Resources}
        afterUpload={handleAfterUpload}
        onError={(msg) => setUploadError(msg)}
      />
      {uploadError && <CustomText useTranslationText={false} text={uploadError} style={styles.errorText} />}
      {renderFileList()}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Video URL" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Paste either a YouTube or Vimeo link here" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="videoUrl"
        control={control}
        rules={{
          validate: (value) => {
            if (!value) return true;
            const isYouTube = YOUTUBE_REGEX.test(value.trim());
            const isVimeo = VIMEO_REGEX.test(value.trim());
            return isYouTube || isVimeo || "Please enter a valid YouTube or Vimeo link.";
          },
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Paste either a YouTube or Vimeo link here"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            style={undefined}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.toggleContainer}>
        <CustomText text="Enable student file upload" size="m" weight="ultraBold" />
        <CustomSpacing type="horizontal" size="s" />
        <Controller
          name="enableFileUpload"
          control={control}
          render={({ field: { onChange, value } }) => <Switch value={value} onValueChange={onChange} />}
        />
      </View>

      <CustomSpacing type="vertical" size="m" />
      <View style={styles.activityState}>
        <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Publish this activity now or schedule a publish date."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />
        <Controller
          control={control}
          name="activityState"
          render={({ field: { onChange, value } }) => (
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              {Object.keys(ActivityState).map((state) => (
                <CustomButton
                  key={state}
                  text={ActivityState[state]}
                  size="s"
                  style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                  onPress={() => {
                    onChange(ActivityState[state]);
                  }}
                />
              ))}
            </div>
          )}
        />
        {watchActivityState === ActivityState.Scheduled && (
          <>
            <CustomText
              text="This Activity will automatically be published after:"
              size="s"
              useTranslationText={false}
              style={styles.stepDescription}
            />
            <Controller
              control={control}
              name="scheduledDate"
              render={({ field: { onChange, value } }) => (
                <div style={{ maxWidth: 250, marginTop: 10 }}>
                  <DatePickerInput
                    locale="en"
                    value={value instanceof Date ? value : value ? new Date(value) : undefined}
                    onChange={onChange}
                    inputMode="start"
                    mode="outlined"
                    outlineColor={"#D1D5DB"}
                    selectionColor={undefined}
                    activeOutlineColor={"blue"}
                    withDateFormatInLabel={false}
                    placeholder="MM/DD/YYYY"
                    theme={{
                      colors: {
                        primary: "#000000",
                        onSurface: "#667085",
                      },
                      roundness: 8,
                    }}
                    style={styles.datePicker}
                    aria-label="Schedule published date picker"
                  />
                </div>
              )}
            />
          </>
        )}
      </View>

      {worksheetFormOpen === "error" && (
        <CustomText
          text="Please fill in all required fields before adding a worksheet."
          style={styles.errorText}
          size="s"
        />
      )}
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  editor: {
    minHeight: 200,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 4,
  },
  toolbar: {
    backgroundColor: "#f1f1f1",
    borderColor: "#ccc",
    borderTopWidth: 1,
  },
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  toggleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  uploadedFile: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 16,
    marginTop: 16,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
});

export default AssignmentForm;
